import { supabase } from '../config/supabaseClient';
import { backendRequest } from './backendApi';
import { getCurrentAccess } from './accessService';

// User Management Service - Handles admin operations via Supabase Edge Functions
class UserManagementService {
	async getAllUsers(tenantId) {
		try {
			let targetTenantId = tenantId;
			if (!targetTenantId) {
				const memberships = await this.getCurrentUserTenantMemberships();
				targetTenantId = memberships.memberships?.[0]?.tenant_id;
			}

			if (!targetTenantId) {
				return { users: [], error: 'No tenant membership found for current user' };
			}

			try {
				const data = await backendRequest(`/admin/rbac/users?tenant_id=${encodeURIComponent(targetTenantId)}`);
				return {
					users: data.users || [],
					error: null,
				};
			} catch (backendError) {
				console.warn('Error fetching users from backend, falling back to direct Supabase lookup:', backendError);
			}

			const { data: membershipRows, error: membershipError } = await supabase
				.from('tenant_memberships')
				.select('user_id')
				.eq('tenant_id', targetTenantId);

			if (membershipError) {
				throw membershipError;
			}

			const userIds = [...new Set((membershipRows || []).map(item => item.user_id).filter(Boolean))];
			if (userIds.length === 0) {
				return { users: [], error: null };
			}

			const usersById = new Map();
			const registerRows = (rows = []) => {
				rows.forEach((row) => {
					const id = row.id || row.uid || row.user_id;
					if (!id) {
						return;
					}

					const existing = usersById.get(String(id)) || {};
					usersById.set(String(id), {
						...existing,
						...row,
						id,
						email: row.email || row.email_address || existing.email || null,
					});
				});
			};

			const candidateColumns = ['id', 'uid', 'user_id'];
			for (const column of candidateColumns) {
				const { data, error } = await supabase
					.from('users')
					.select('id, uid, user_id, email, email_address')
					.in(column, userIds);

				if (!error && Array.isArray(data)) {
					registerRows(data);
				}
			}

			const users = userIds.map((id) => {
				const found = usersById.get(String(id));
				if (found) {
					return found;
				}

				return { id, uid: id, email: null };
			});

			return { users, error: null };
		} catch (error) {
			console.error('Error fetching users directly from Supabase:', error);
			return {
				users: [],
				error: error.message || 'Failed to fetch users from Supabase',
			};
		}
	}

	async createUser(email, password, metadata = {}, tenantId) {
		try {
			let targetTenantId = tenantId;
			if (!targetTenantId) {
				const memberships = await this.getCurrentUserTenantMemberships();
				targetTenantId = memberships.memberships?.[0]?.tenant_id;
			}

			if (!targetTenantId) {
				throw new Error('No tenant membership found for current user');
			}

			const data = await backendRequest('/admin/rbac/users', {
				method: 'POST',
				body: {
					tenant_id: targetTenantId,
					email,
					password,
					app_metadata: metadata.app_metadata || {},
					user_metadata: metadata.user_metadata || {},
				},
			});

			return {
				user: data.user,
				error: null,
			};
		} catch (error) {
			console.error('Error creating user:', error);
			return {
				user: null,
				error: error.message || 'Failed to create user',
			};
		}
	}

	async deleteUser(userId, tenantId) {
		try {
			let targetTenantId = tenantId;
			if (!targetTenantId) {
				const memberships = await this.getCurrentUserTenantMemberships();
				targetTenantId = memberships.memberships?.[0]?.tenant_id;
			}

			if (!targetTenantId) {
				throw new Error('No tenant membership found for current user');
			}

			await backendRequest(`/admin/rbac/users/${userId}?tenant_id=${encodeURIComponent(targetTenantId)}`, {
				method: 'DELETE',
			});

			return { error: null };
		} catch (error) {
			console.error('Error deleting user:', error);
			return {
				error: error.message || 'Failed to delete user',
			};
		}
	}

	async resetUserPassword(userId, newPassword, tenantId) {
		try {
			let targetTenantId = tenantId;
			if (!targetTenantId) {
				const memberships = await this.getCurrentUserTenantMemberships();
				targetTenantId = memberships.memberships?.[0]?.tenant_id;
			}

			if (!targetTenantId) {
				throw new Error('No tenant membership found for current user');
			}

			const data = await backendRequest(`/admin/rbac/users/${userId}/reset-password`, {
				method: 'POST',
				body: {
					tenant_id: targetTenantId,
					new_password: newPassword,
				},
			});

			return {
				user: data.user,
				error: null,
			};
		} catch (error) {
			console.error('Error resetting password:', error);
			return {
				user: null,
				error: error.message || 'Failed to reset password',
			};
		}
	}

	async getCurrentUserTenantMemberships(userId) {
		try {
			const data = await backendRequest('/admin/rbac/me/memberships');
			const filtered = userId
				? (data.memberships || []).filter(item => item.user_id === userId)
				: (data.memberships || []);
			return { memberships: filtered, error: null };
		} catch (error) {
			try {
				if (!userId) {
					throw error;
				}
				const { data, error: fallbackError } = await supabase
					.from('tenant_memberships')
					.select('tenant_id, user_id, role_id, role, created_at, tenants(id, name), tenant_roles(id, name, description, is_system)')
					.eq('user_id', userId)
					.order('created_at', { ascending: true });

				if (fallbackError) {
					throw fallbackError;
				}

				return { memberships: data || [], error: null };
			} catch (fallback) {
				console.error('Error loading current user memberships:', fallback);
				return { memberships: [], error: fallback.message || 'Failed to load memberships' };
			}
		}
	}

	async getCurrentUserAccess() {
		try {
			const data = await getCurrentAccess({ forceRefresh: true });
			return {
				access: {
					user_id: data.user_id || null,
					tenant_id: data.tenant_id || null,
					role_id: data.role_id || null,
					role: data.role || null,
					permissions: Array.from(data.permissions || []),
				},
				status: null,
				error: null,
			};
		} catch (error) {
			console.error('Error loading current user access:', error);
			return {
				access: null,
				status: error?.status || null,
				error: error.message || 'Failed to load current user access',
			};
		}
	}

	async getTenantPermissionsCatalog() {
		try {
			const data = await backendRequest('/admin/rbac/permissions');
			return { permissions: data.permissions || [], error: null };
		} catch (error) {
			try {
				const { data, error: fallbackError } = await supabase
					.from('tenant_permissions')
					.select('code, description, created_at')
					.order('code', { ascending: true });

				if (fallbackError) {
					throw fallbackError;
				}

				return { permissions: data || [], error: null };
			} catch (fallback) {
				console.error('Error loading permission catalog:', fallback);
				return { permissions: [], error: fallback.message || 'Failed to load permission catalog' };
			}
		}
	}

	async getTenantRoles(tenantId) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/roles`);
			return { roles: data.roles || [], error: null };
		} catch (error) {
			try {
				const { data, error: fallbackError } = await supabase
					.from('tenant_roles')
					.select('id, tenant_id, name, description, is_system, created_at')
					.eq('tenant_id', tenantId)
					.order('is_system', { ascending: false })
					.order('name', { ascending: true });

				if (fallbackError) {
					throw fallbackError;
				}

				return { roles: data || [], error: null };
			} catch (fallback) {
				console.error('Error loading tenant roles:', fallback);
				return { roles: [], error: fallback.message || 'Failed to load tenant roles' };
			}
		}
	}

	async getTenantRolePermissions(tenantId) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/role-permissions`);
			return {
				rolePermissions: (data.role_permissions || []).map(item => ({
					role_id: item.role_id,
					permission_code: item.permission_code,
					created_at: item.created_at,
				})),
				error: null,
			};
		} catch (error) {
			try {
				const { data, error: fallbackError } = await supabase
					.from('tenant_role_permissions')
					.select('role_id, permission_code, created_at, tenant_roles!inner(tenant_id)')
					.eq('tenant_roles.tenant_id', tenantId);

				if (fallbackError) {
					throw fallbackError;
				}

				return {
					rolePermissions: (data || []).map(item => ({
						role_id: item.role_id,
						permission_code: item.permission_code,
						created_at: item.created_at,
					})),
					error: null,
				};
			} catch (fallback) {
				console.error('Error loading role permissions:', fallback);
				return { rolePermissions: [], error: fallback.message || 'Failed to load role permissions' };
			}
		}
	}

	async getTenantMemberships(tenantId) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/memberships`);
			return { memberships: data.memberships || [], error: null };
		} catch (error) {
			try {
				const { data, error: fallbackError } = await supabase
					.from('tenant_memberships')
					.select('tenant_id, user_id, role_id, role, created_at')
					.eq('tenant_id', tenantId)
					.order('created_at', { ascending: true });

				if (fallbackError) {
					throw fallbackError;
				}

				return { memberships: data || [], error: null };
			} catch (fallback) {
				console.error('Error loading tenant memberships:', fallback);
				return { memberships: [], error: fallback.message || 'Failed to load tenant memberships' };
			}
		}
	}

	async getTenantMemberOverrides(tenantId) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/member-overrides`);
			return { overrides: data.overrides || [], error: null };
		} catch (error) {
			try {
				const { data, error: fallbackError } = await supabase
					.from('tenant_member_permissions')
					.select('tenant_id, user_id, permission_code, effect, created_at')
					.eq('tenant_id', tenantId);

				if (fallbackError) {
					throw fallbackError;
				}

				return { overrides: data || [], error: null };
			} catch (fallback) {
				console.error('Error loading member overrides:', fallback);
				return { overrides: [], error: fallback.message || 'Failed to load member overrides' };
			}
		}
	}

	async createTenantRole(tenantId, payload) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/roles`, {
				method: 'POST',
				body: payload,
			});
			return { role: data.role, error: null };
		} catch (error) {
			console.error('Error creating tenant role:', error);
			return { role: null, error: error.message || 'Failed to create role' };
		}
	}

	async updateTenantRole(roleId, payload) {
		try {
			const data = await backendRequest(`/admin/rbac/roles/${roleId}`, {
				method: 'PATCH',
				body: payload,
			});
			return { role: data.role, error: null };
		} catch (error) {
			console.error('Error updating tenant role:', error);
			return { role: null, error: error.message || 'Failed to update role' };
		}
	}

	async deleteTenantRole(roleId) {
		try {
			await backendRequest(`/admin/rbac/roles/${roleId}`, {
				method: 'DELETE',
			});
			return { error: null };
		} catch (error) {
			console.error('Error deleting tenant role:', error);
			return { error: error.message || 'Failed to delete role' };
		}
	}

	async replaceRolePermissions(roleId, permissionCodes) {
		try {
			await backendRequest(`/admin/rbac/roles/${roleId}/permissions`, {
				method: 'PUT',
				body: {
					permission_codes: permissionCodes,
				},
			});
			return { error: null };
		} catch (error) {
			console.error('Error replacing role permissions:', error);
			return { error: error.message || 'Failed to update role permissions' };
		}
	}

	async createTenantMembership(tenantId, userId, roleId, roleName) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/memberships`, {
				method: 'POST',
				body: {
					user_id: userId,
					role_id: roleId,
					role: roleName,
				},
			});
			return { membership: data.membership, error: null };
		} catch (error) {
			console.error('Error creating tenant membership:', error);
			return { membership: null, error: error.message || 'Failed to add member' };
		}
	}

	async updateTenantMembershipRole(tenantId, userId, roleId, roleName) {
		try {
			const data = await backendRequest(`/admin/rbac/tenants/${tenantId}/memberships/${userId}`, {
				method: 'PATCH',
				body: {
					role_id: roleId,
					role: roleName,
				},
			});
			return { membership: data.membership, error: null };
		} catch (error) {
			console.error('Error updating tenant membership role:', error);
			return { membership: null, error: error.message || 'Failed to update member role' };
		}
	}

	async removeTenantMembership(tenantId, userId) {
		try {
			await backendRequest(`/admin/rbac/tenants/${tenantId}/memberships/${userId}`, {
				method: 'DELETE',
			});
			return { error: null };
		} catch (error) {
			console.error('Error removing tenant membership:', error);
			return { error: error.message || 'Failed to remove member' };
		}
	}

	async replaceMemberOverrides(tenantId, userId, entries) {
		try {
			await backendRequest(`/admin/rbac/tenants/${tenantId}/members/${userId}/overrides`, {
				method: 'PUT',
				body: { entries },
			});
			return { error: null };
		} catch (error) {
			console.error('Error replacing member overrides:', error);
			return { error: error.message || 'Failed to update member overrides' };
		}
	}

	async getDeploymentApiKeyPermissions(tenantId) {
		try {
			const suffix = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
			const data = await backendRequest(`/deployment/api-key-permissions${suffix}`);
			return { permissions: data.items || [], error: null };
		} catch (error) {
			console.error('Error loading deployment API key permissions:', error);
			return { permissions: [], error: error.message || 'Failed to load deployment API key permissions' };
		}
	}

	async listDeploymentApiKeys(tenantId) {
		try {
			const suffix = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
			const data = await backendRequest(`/deployment/api-keys${suffix}`);
			return { apiKeys: data.items || [], error: null };
		} catch (error) {
			console.error('Error loading deployment API keys:', error);
			return { apiKeys: [], error: error.message || 'Failed to load deployment API keys' };
		}
	}

	async createDeploymentApiKey(tenantId, payload) {
		try {
			const data = await backendRequest('/deployment/api-keys', {
				method: 'POST',
				body: {
					tenant_id: tenantId,
					...payload,
				},
			});
			return { apiKey: data, error: null };
		} catch (error) {
			console.error('Error creating deployment API key:', error);
			return { apiKey: null, error: error.message || 'Failed to create deployment API key' };
		}
	}

	async updateDeploymentApiKey(tenantId, apiKeyId, payload) {
		try {
			const data = await backendRequest(`/deployment/api-keys/${apiKeyId}`, {
				method: 'PATCH',
				body: {
					tenant_id: tenantId,
					...payload,
				},
			});
			return { apiKey: data, error: null };
		} catch (error) {
			console.error('Error updating deployment API key:', error);
			return { apiKey: null, error: error.message || 'Failed to update deployment API key' };
		}
	}

	async deleteDeploymentApiKey(tenantId, apiKeyId) {
		try {
			const suffix = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
			await backendRequest(`/deployment/api-keys/${apiKeyId}${suffix}`, {
				method: 'DELETE',
			});
			return { error: null };
		} catch (error) {
			console.error('Error deleting deployment API key:', error);
			return { error: error.message || 'Failed to delete deployment API key' };
		}
	}
}

// Export singleton instance
export const userManagementService = new UserManagementService();
